import React, { useEffect, useMemo, useRef, useState } from 'react';
import CallerInformationTab from './CallerInformation';
import { API_BASE } from '../../config/api.js';

const SINDH_GOV_DEPARTMENTS = [
  '--Select--',
  'Law & Order',
  'Agri Water Issue',
  'Drinking Water Issue',
  'Land Dispute',
  'Land Enchrochment',
  'Elct_Gas Utilities',
  'Group Insaurance',
  'Illegal Construction',
  'Financial Support',
  'Sport Facility',
  'Minority Issues',
  'Right to Education',
  'Health Issue',
  'Other',
  'Federal government',
  'N/A',
  'Other Province',
];

const APPROVAL_FILTER_STATUSES = [
  '--Select--',
  'ALL',
  'Initiated',
  'Closed',
  'Completed',
  'Refer',
  'Approved',
  'Corrected',
  'Advised',
];

const INITIAL_APPROVAL_FILTERS = {
  dateFrom: '',
  dateTo: '',
  status: '',
};

const APPROVAL_PAGE_SIZE = 20;

const normalizeApprovalStatusFilter = (value) => {
  const normalized = String(value || '').trim();
  return !normalized || normalized === '--Select--' ? '' : normalized;
};

const extractApprovalResponseList = (json) => {
  if (Array.isArray(json?.data?.data)) return json.data.data;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.result)) return json.result;
  if (Array.isArray(json?.results)) return json.results;
  if (Array.isArray(json)) return json;
  return [];
};

const normalizeApprovalFilterRow = (row = {}) => ({
  queryId: row.QID ?? row.QueryID ?? row.qid ?? row.QueryId ?? row.queryId ?? row.queryid ?? row.ComplaintID ?? row.complaintid ?? '',
  callerId: row.CallersID ?? row.CallerID ?? row.callersId ?? row.callerId ?? row.callersid ?? row.callerid ?? '',
  contactPerson:
    row.ContactPerson ??
    row.contactperson ??
    row.contactPerson ??
    row.ContactPersonName ??
    row.contactPersonName ??
    row.CallerName ??
    row.callerName ??
    row.Name ??
    row.name ??
    '',
  status:
    row.Status ??
    row.status ??
    row.QueryStatus ??
    row.querystatus ??
    row.queryStatus ??
    row.JobStatus ??
    row.jobstatus ??
    row.jobStatus ??
    '',
  priority: row.Priority ?? row.priority ?? row.PriorityName ?? row.priorityName ?? '',
  contactNumber:
    row.ContactNumber ??
    row.contactnumber ??
    row.contactNumber ??
    row.PhoneNo ??
    row.phoneno ??
    row.phoneNo ??
    row.MobileNo ??
    row.mobileno ??
    row.mobileNo ??
    row.CellNo ??
    row.cellno ??
    row.CLI ??
    row.cli ??
    '',
  routedToDepartment: row.RoutedToDepartment ?? row.routedToDepartment ?? row.routedtodepartment ?? '',
  edited: row.Edited ?? row.edited ?? '',
  mainCat: row.MainCat ?? row.mainCat ?? row.Category ?? row.category ?? '',
  cat: row.Cat ?? row.cat ?? row.SubCategory ?? row.subCategory ?? '',
  subCat: row['Sub-Cat'] ?? row['sub-cat'] ?? row.SubCatChild ?? row.subCatChild ?? row.subCat ?? '',
  office: row.Office ?? row.office ?? '',
  createdOn: row.CreatedOn ?? row.createdOn ?? row.createdon ?? '',
  modifiedOn: row.ModifiedOn ?? row.modifiedOn ?? row.modifiedon ?? '',
  closedOn: row.ClosedOn ?? row.closedOn ?? row.closedon ?? '',
  history: row.History ?? row.history ?? '',
  notes: row.Notes ?? row.notes ?? '',
  tags: row.TAGS ?? row.Tags ?? row.tags ?? '',
  createdBy: row.CreatedBy ?? row.createdby ?? row.createdByName ?? row.createdByname ?? row.createdBy ?? row.Created ?? row.created ?? '',
  modifiedBy: row.ModifyiedBy ?? row.ModifiedBy ?? row.modifiedBy ?? row.modifiedby ?? '',
  routedOn: row.RoutedOn ?? row.routedOn ?? row.routedon ?? '',
  routedBy: row.RoutedBy ?? row.routeBy ?? row.routedBy ?? row.routeby ?? row.routedby ?? '',
  shortQuery: row.ShortQuery ?? row.shortQuery ?? row.additionalInfo8 ?? row.AdditionalInfo8 ?? '',
  __raw: row,
});

const buildApprovalFilterPayload = ({ filters, departmentName, currentUser, currentUserId }) => {
  const payload = {};

  if (departmentName) {
    payload.departmentName = departmentName;
    payload.departmentname = departmentName;
    payload.departmantname = departmentName;
  }

  if (currentUser) {
    payload.currentUser = currentUser;
    payload.userName = currentUser;
    payload.username = currentUser;
  }

  if (currentUserId) {
    payload.currentUserId = currentUserId;
    payload.userId = currentUserId;
    payload.UserID = currentUserId;
  }

  if (filters?.dateFrom) {
    payload.dateFrom = filters.dateFrom;
    payload.DateFrom = filters.dateFrom;
  }

  if (filters?.dateTo) {
    payload.dateTo = filters.dateTo;
    payload.DateTo = filters.dateTo;
  }

  const status = normalizeApprovalStatusFilter(filters?.status);
  if (status) {
    payload.status = status;
    payload.Status = status;
    payload.jobStatus = status;
    payload.JobStatus = status;
    payload.acceptStatus = status;
  }

  return payload;
};

const BackofficeApprovalPanel = ({
  isActive,
  rows,
  selectedApprovalId,
  onSelectApproval,
  selectedApproval: selectedApprovalProp,
  currentUserId,
  reviewScreen,
  onReviewScreenChange,
  getRowKey,
  statusToClass,
  onCloseGI,
  onRefreshRows,
  onApprovalFilterActiveChange,
  queryCurrentPage = 1,
  queryTotalPages = 1,
  queryTotalCount = 0,
  queryLoading = false,
  onQueryPageChange,
  isViewOnly = false,
}) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyErr, setHistoryErr] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [departmentSelection, setDepartmentSelection] = useState('--Select--');
  const [approvalFilters, setApprovalFilters] = useState(INITIAL_APPROVAL_FILTERS);
  const [approvalRows, setApprovalRows] = useState([]);
  const [approvalFilterApplied, setApprovalFilterApplied] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalErr, setApprovalErr] = useState('');
  const [selectedLawyer, setSelectedLawyer] = useState('all');
  const [approvalPage, setApprovalPage] = useState(1);
  const [approvalTotal, setApprovalTotal] = useState(0);
  const approvalPayloadRef = useRef(null);
  const previousQueryRowsRef = useRef(rows);

  useEffect(() => {
    if (previousQueryRowsRef.current === rows) return;
    previousQueryRowsRef.current = rows;
    if (!approvalFilterApplied) return;

    setApprovalRows([]);
    setApprovalFilterApplied(false);
    setApprovalPage(1);
    setApprovalTotal(0);
    approvalPayloadRef.current = null;
    onApprovalFilterActiveChange?.(false);
  }, [approvalFilterApplied, onApprovalFilterActiveChange, rows]);

  const activeScreen = reviewScreen || 'advisor';
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const sessionDepartment =
    sessionUser?.DepartmentName ||
    sessionUser?.departmentName ||
    '';
  const currentUserName =
    sessionUser?.FullName ||
    sessionUser?.UserName ||
    sessionUser?.username ||
    sessionUser?.LoginName ||
    '';
  const effectiveCurrentUserId =
    currentUserId ||
    sessionUser?.UserID ||
    sessionUser?.userId ||
    '';
  const queueSourceRows = approvalFilterApplied ? approvalRows : normalizedRows;
  const filteredSelectedApproval = queueSourceRows.find((row) => String(getRowKey?.(row) || row?.queryId || row?.QueryID || '') === String(selectedApprovalId || '')) || null;
  const selectedApproval = useMemo(() => {
    if (filteredSelectedApproval) return filteredSelectedApproval;
    if (!approvalFilterApplied) return selectedApprovalProp || null;
    const propKey = String(getRowKey?.(selectedApprovalProp) || selectedApprovalProp?.queryId || selectedApprovalProp?.QueryID || '');
    if (selectedApprovalProp && propKey && propKey === String(selectedApprovalId || '')) {
      return selectedApprovalProp;
    }
    return null;
  }, [filteredSelectedApproval, approvalFilterApplied, selectedApprovalProp, selectedApprovalId, getRowKey]);
  const selectedQid = useMemo(() => {
    const value = selectedApproval?.queryId || selectedApproval?.QueryID || selectedApprovalId || '';
    return String(value).trim();
  }, [selectedApproval, selectedApprovalId]);
  const selectedApprovalStatus = String(selectedApproval?.status || selectedApproval?.Status || '').trim();
  const selectedApprovalModifiedOn = String(
    selectedApproval?.modifiedOn ||
    selectedApproval?.ModifiedOn ||
    selectedApproval?.closedOn ||
    selectedApproval?.ClosedOn ||
    ''
  ).trim();

  useEffect(() => {
    if (!isActive) {
      setDetail(null);
      setDetailErr('');
      setDetailLoading(false);
      return;
    }
    if (!selectedQid) {
      setDetail(null);
      setDetailErr('');
      return;
    }

    const ac = new AbortController();
    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        setDetailErr('');
        const [detailResult, selectionsResult] = await Promise.allSettled([
          fetch(
            `${API_BASE}/Bocomplaints/${encodeURIComponent(selectedQid)}/detail`,
            { headers: { Accept: 'application/json' }, signal: ac.signal }
          ),
          fetch(
            `${API_BASE}/complaints/${encodeURIComponent(selectedQid)}/selections`,
            { headers: { Accept: 'application/json' }, signal: ac.signal }
          ),
        ]);
        if (detailResult.status === 'rejected') throw detailResult.reason;
        const res = detailResult.value;
        const json = await res.json();
        const raw = Array.isArray(json?.data) ? json.data[0] : (json?.data ?? null);
        if (!raw) {
          setDetail(null);
          setDetailErr('No detail record found.');
          return;
        }
        let selections = {};
        if (selectionsResult.status === 'fulfilled' && selectionsResult.value.ok) {
          try {
            const selectionsJson = await selectionsResult.value.json();
            selections = Array.isArray(selectionsJson?.data)
              ? (selectionsJson.data[0] || {})
              : (selectionsJson?.data ?? selectionsJson ?? {});
          } catch {
            selections = {};
          }
        }
        const readSelection = (...keys) => {
          for (const key of keys) {
            const value = selections?.[key];
            if (Array.isArray(value) && value.length) return value.join(', ');
            if (value !== undefined && value !== null && String(value).trim()) return value;
          }
          return '';
        };
        const mapped = {
          jobStatus: raw.JobStatus ?? raw.jobStatus ?? '',
          createdOn: raw.CreatedOn ?? raw.createdOn ?? '',
          createdBy: raw.Created ?? raw.created ?? raw.CreatedBy ?? raw.createdBy ?? '',
          query: raw.Query ?? raw.query ?? '',
          solution: raw.Solution ?? raw.solution ?? '',
          category: raw.Category ?? raw.category ?? '',
          subCategory: raw.SubCategory ?? raw.subCategory ?? '',
          subCatChild: raw.SubCatChild ?? raw.subCatChild ?? '',
          categoryId: raw.CategoryID ?? raw.categoryId ?? raw.CategoryId ?? '',
          subCategoryId: raw.SubCategoryID ?? raw.subCategoryId ?? raw.SubCategoryId ?? '',
          sms: raw.SMS ?? raw.sms ?? '',
          resolutionProvided: raw.ResolutionProvided ?? raw.resolutionProvided ?? '',
          routedToDepartment: raw.RoutedToDepartment ?? raw.routedToDepartment ?? '',
          routedBy: raw.RoutedBy ?? raw.routedBy ?? '',
          routedOn: raw.RoutedOn ?? raw.routedOn ?? '',
          modifiedOn: raw.ModifiedOn ?? raw.modifiedOn ?? '',
          modifiedBy: raw.Modified ?? raw.modified ?? '',
          closedOn: raw.ClosedOn ?? raw.closedOn ?? '',
          closedBy: raw.Closed ?? raw.closed ?? '',
          additionalInfo1: raw.AdditionalInfo1 ?? raw.additionalinfo1 ?? raw.additionalInfo1 ?? '',
          additionalInfo2: raw.AdditionalInfo2 ?? raw.additionalinfo2 ?? raw.additionalInfo2 ?? '',
          additionalInfo3: raw.AdditionalInfo3 ?? raw.additionalinfo3 ?? raw.additionalInfo3 ?? '',
          covid: raw.COVID ?? raw.covid ?? '',
          ageAboveFifty: raw.AgeAboveFifty ?? raw.ageAboveFifty ?? '',
          domesticViolence: raw.DomesticViolence ?? raw.Domesticviolence ?? raw.domesticViolence ?? '',
          womenProperty: raw.WomenProperty ?? raw.womenProperty ?? '',
          mainTags:
            readSelection('MainTags', 'mainTags', 'maintags', 'OldTags', 'oldTags', 'oldtags') ||
            (raw.MainTags ?? raw.mainTags ?? raw.maintags ?? ''),
          tagMfl:
            readSelection('TagMFL', 'tagMFL', 'tagMfl', 'tagmfl') ||
            (raw.TagMFL ?? raw.tagMFL ?? raw.tagMfl ?? raw.tagmfl ?? ''),
          tagWrlmp:
            readSelection('TagWRLMP', 'tagWRLMP', 'tagWrlmp', 'tagwrlmp') ||
            (raw.TagWRLMP ?? raw.tagWRLMP ?? raw.tagWrlmp ?? raw.tagwrlmp ?? ''),
          furtherHelp: raw.FurtherHelp ?? raw.furtherHelp ?? '',
          sindhGovDept: raw.SindhGovDept ?? raw.sindhGovDept ?? '',
          priority: raw.Priority ?? raw.priority ?? '',
          shortQuery: raw.ShortQuery ?? raw.shortQuery ?? '',
        };
        setDetail(mapped);
      } catch (err) {
        if (ac.signal.aborted) return;
        setDetail(null);
        setDetailErr(err?.message || 'Failed to load detail');
      } finally {
        if (!ac.signal.aborted) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => ac.abort();
  }, [isActive, selectedApprovalModifiedOn, selectedApprovalStatus, selectedQid]);

  useEffect(() => {
    if (!isActive) {
      setHistoryRows([]);
      setHistoryErr('');
      setHistoryLoading(false);
      return;
    }
    if (activeScreen !== 'history') return;
    if (!selectedQid) {
      setHistoryRows([]);
      setHistoryErr('');
      return;
    }

    const ac = new AbortController();
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        setHistoryErr('');
        const res = await fetch(
          `${API_BASE}/Bocomplaints/${encodeURIComponent(selectedQid)}/history`,
          { headers: { Accept: 'application/json' }, signal: ac.signal }
        );
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        const mapped = list.map((row) => ({
          queryId: row.QueryID ?? row.queryid ?? row.complaintid ?? row.ComplaintID ?? '',
          status: row.QueryStatus ?? row.querystatus ?? row.jobstatus ?? row.JobStatus ?? '',
          createdOn: row.CreatedOn ?? row.createdon ?? '',
          createdBy: row.CreatedBy ?? row.createdby ?? row.created ?? '',
          modifiedOn: row.ModifiedOn ?? row.modifiedon ?? '',
          modifiedBy: row.ModifiedBy ?? row.modifiedby ?? row.modified ?? '',
          closedOn: row.ClosedOn ?? row.closedon ?? '',
          closedBy: row.ClosedBy ?? row.closedby ?? row.closed ?? '',
          shortQuery: row.ShortQuery ?? row.shortquery ?? row.additionalinfo8 ?? row.AdditionalInfo8 ?? '',
          query: row.Query ?? row.query ?? row.additionalinfo9 ?? row.AdditionalInfo9 ?? '',
          solution: row.Solution ?? row.solution ?? row.additionalinfo10 ?? row.AdditionalInfo10 ?? '',
        }));
        if (!ac.signal.aborted) setHistoryRows(mapped);
      } catch (err) {
        if (ac.signal.aborted) return;
        setHistoryRows([]);
        setHistoryErr(err?.message || 'Failed to load history');
      } finally {
        if (!ac.signal.aborted) setHistoryLoading(false);
      }
    };

    loadHistory();
    return () => ac.abort();
  }, [isActive, activeScreen, selectedApprovalModifiedOn, selectedApprovalStatus, selectedQid]);

  const queueRows = useMemo(() => {
    const lawyer = String(selectedLawyer || 'all').trim().toLowerCase();
    if (!lawyer || lawyer === 'all') return queueSourceRows;
    return queueSourceRows.filter((row) => {
      const rowLawyer = String(row.createdBy || row.CreatedBy || row.routedBy || row.RoutedBy || '').trim().toLowerCase();
      return rowLawyer === lawyer;
    });
  }, [queueSourceRows, selectedLawyer]);

  const queuePendingCount = queueRows.filter((row) =>
    String(row?.status || row?.Status || '').trim().toLowerCase().includes('pending')
  ).length;
  const approvalTotalPages = Math.max(1, Math.ceil(approvalTotal / APPROVAL_PAGE_SIZE));
  const queueCurrentPage = approvalFilterApplied ? approvalPage : queryCurrentPage;
  const queueTotalPages = approvalFilterApplied ? approvalTotalPages : queryTotalPages;
  const queueTotalCount = approvalFilterApplied ? approvalTotal : queryTotalCount;
  const queuePageLoading = approvalFilterApplied ? approvalLoading : queryLoading;

  const selectedStatus = detail?.jobStatus || selectedApproval?.status || selectedApproval?.Status || 'Pending Review';
  const selectedStatusClass = statusToClass?.(selectedStatus);
  const selectedStatusNormalized = String(selectedStatus || '').trim().toLowerCase();
  const isCompletedStatus = selectedStatusNormalized === 'completed' || selectedStatusNormalized === 'complete';
  const isCorrectedStatus = selectedStatusNormalized === 'corrected';
  const isAdvisedStatus = selectedStatusNormalized === 'advised';
  const isInitiatedStatus = selectedStatusNormalized === 'initiated';
  const isFinalStatus = ['closed', 'approved', 'refer', 'reffer', 'referred'].includes(selectedStatusNormalized);
  const normalizeTag = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const isDisplayTag = (value) => {
    const tag = String(value ?? '').trim();
    if (!tag || /^\d+$/.test(tag)) return false;
    const normalized = tag.toLowerCase();
    return !['true', 'false', 'yes', 'no', 'checked', 'unchecked', 'null', 'undefined'].includes(normalized);
  };
  const splitDisplayTags = (value) =>
    String(value ?? '')
      .split(/[;,|]/)
      .map((tag) => tag.trim())
      .filter(isDisplayTag);
  const asTrue = (val) => {
    const s = String(val ?? '').trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'checked';
  };
  const detailTags = [
    detail?.mainTags,
    detail?.tagMfl,
    detail?.tagWrlmp,
    detail?.additionalInfo1,
    detail?.additionalInfo2,
    detail?.covid,
    detail?.domesticViolence,
    detail?.womenProperty,
    detail?.ageAboveFifty,
  ].flatMap(splitDisplayTags);
  if (asTrue(detail?.shortQuery)) detailTags.push('Short Query');

  const uniqueTags = (tags) => {
    const seen = new Set();
    return tags.filter((tag) => {
      const key = normalizeTag(tag);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const tagList = detailTags.length
    ? uniqueTags(detailTags)
    : splitDisplayTags(selectedApproval?.tags || selectedApproval?.TAGS || '');
  const displayTags = tagList;
  const tagSet = new Set(tagList.map(normalizeTag));
  const hasTagData = tagList.length > 0;
  const shortQueryValue =
    detail?.shortQuery ??
    selectedApproval?.shortQuery ??
    selectedApproval?.ShortQuery ??
    selectedApproval?.additionalInfo8 ??
    selectedApproval?.AdditionalInfo8 ??
    '';
  const isShortQuery = asTrue(shortQueryValue);

  let showRefer = true;
  let showClose = true;
  let closeLabel = 'Close';
  let showApprovedButton = !isCompletedStatus && !isFinalStatus && !isInitiatedStatus;
  let showRejectButton = !isCompletedStatus && !isFinalStatus && !isInitiatedStatus;

  if (isInitiatedStatus || isFinalStatus) {
    showRefer = false;
    showClose = false;
    showApprovedButton = false;
    showRejectButton = false;
  }

  if (isAdvisedStatus) {
    showRefer = true;
    showClose = true;
    closeLabel = 'Close';
    showApprovedButton = false;
    showRejectButton = false;
  }

  if (isCorrectedStatus || isCompletedStatus) {
    showRefer = true;
    showClose = true;
    closeLabel = isShortQuery ? 'Close' : 'Approved';
    showApprovedButton = !isCompletedStatus && closeLabel !== 'Approved';
    showRejectButton = !isCompletedStatus;
  }

  const lawyerOptions = Array.from(
    new Set(
      queueSourceRows
        .map((row) => row.createdBy || row.CreatedBy || row.routedBy || row.RoutedBy || '')
        .map((value) => String(value).trim())
        .filter(Boolean)
    )
  );
  const routedToDepartmentName = String(
    sessionDepartment ||
    detail?.routedToDepartment ||
    selectedApproval?.routedToDepartment ||
    selectedApproval?.RoutedToDepartment ||
    ''
  ).trim();

  const departmentValue =
    detail?.sindhGovDept ||
    selectedApproval?.sindhGovDept ||
    selectedApproval?.SindhGovDept ||
       selectedApproval?.office ||
    selectedApproval?.Office ||
    sessionDepartment ||
    '';

  useEffect(() => {
    setDepartmentSelection(departmentValue || '--Select--');
  }, [departmentValue, selectedQid]);

  const departmentOptions = useMemo(() => {
    const list = [...SINDH_GOV_DEPARTMENTS];
    const trimmed = String(departmentSelection || '').trim();
    const hasCurrent =
      trimmed &&
      list.some((dept) => dept.toLowerCase() === trimmed.toLowerCase());
    if (trimmed && !hasCurrent) {
      list.unshift(trimmed);
    }
    return list;
  }, [departmentSelection]);

  const reviewMainCategory = detail?.category || selectedApproval?.mainCat || selectedApproval?.MainCat || '';
  const reviewCategory = detail?.subCategory || selectedApproval?.cat || selectedApproval?.Cat || '';
  const reviewSubCategory = detail?.subCatChild || selectedApproval?.subCat || selectedApproval?.SubCat || '';
  const reviewPriority = detail?.priority || selectedApproval?.priority || selectedApproval?.Priority || '';
  const reviewSubmittedBy = detail?.createdBy || selectedApproval?.createdBy || selectedApproval?.CreatedBy || '';
  const reviewSubmittedOn = detail?.createdOn || selectedApproval?.createdOn || selectedApproval?.CreatedOn || '';
  const reviewQuery = detail?.query || selectedApproval?.query || '';
  const reviewSolution = detail?.solution || selectedApproval?.solution || '';
  const reviewCaller = selectedApproval?.contactNumber || selectedApproval?.ContactNumber || selectedApproval?.contactPerson || selectedApproval?.ContactPerson || '';
  const [actionBusy, setActionBusy] = useState(false);
  const computernameDetails =
    detail?.computernameDetails ||
    selectedApproval?.computernameDetails ||
    selectedApproval?.ComputernameDetails ||
    '';

  const loadApprovalQueue = async (payload, page = 1) => {
    try {
      setApprovalLoading(true);
      setApprovalErr('');
      const res = await fetch(`${API_BASE}/history-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, page, perPage: APPROVAL_PAGE_SIZE }),
      });
      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch {}
      if (!res.ok) throw new Error(json?.message || json?.error || text || 'Failed to load advisor approvals');

      const mapped = extractApprovalResponseList(json).map((row) => normalizeApprovalFilterRow(row));
      const total = Number(json?.total ?? json?.totalCount ?? json?.Total ?? json?.TotalCount ?? mapped.length);
      setApprovalRows(mapped);
      setApprovalFilterApplied(true);
      setApprovalPage(page);
      setApprovalTotal(Number.isFinite(total) ? total : mapped.length);
      onApprovalFilterActiveChange?.(true);
      if (mapped.length) {
        onSelectApproval?.(String(getRowKey?.(mapped[0]) || mapped[0].queryId || mapped[0].QueryID || ''));
      } else {
        onSelectApproval?.('');
      }
      return mapped;
    } catch (error) {
      setApprovalErr(error?.message || 'Failed to load advisor approvals');
      return null;
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalFilterChange = (event) => {
    const { name, value } = event.target;
    setApprovalErr('');
    setApprovalFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApprovalFilterApply = async () => {
    const payload = buildApprovalFilterPayload({
      filters: approvalFilters,
      departmentName: sessionDepartment,
      currentUser: currentUserName,
      currentUserId: effectiveCurrentUserId,
    });
    approvalPayloadRef.current = payload;
    await loadApprovalQueue(payload, 1);
  };

  const handleApprovalFilterReset = () => {
    setApprovalFilters(INITIAL_APPROVAL_FILTERS);
    setApprovalRows([]);
    setApprovalFilterApplied(false);
    setApprovalPage(1);
    setApprovalTotal(0);
    approvalPayloadRef.current = null;
    onApprovalFilterActiveChange?.(false);
    setApprovalErr('');
    setSelectedLawyer('all');
    if (normalizedRows.length) {
      onSelectApproval?.(String(getRowKey?.(normalizedRows[0]) || normalizedRows[0]?.queryId || normalizedRows[0]?.QueryID || ''));
    }
  };

  const refreshApprovalQueue = async () => {
    if (approvalFilterApplied) {
      const payload = approvalPayloadRef.current || buildApprovalFilterPayload({
          filters: approvalFilters,
          departmentName: sessionDepartment,
          currentUser: currentUserName,
          currentUserId: effectiveCurrentUserId,
        });
      approvalPayloadRef.current = payload;
      await loadApprovalQueue(payload, approvalPage);
      return;
    }
    await onRefreshRows?.();
  };

  const handleQueuePageChange = async (page) => {
    if (page < 1 || page > queueTotalPages || queuePageLoading) return;

    if (approvalFilterApplied) {
      if (!approvalPayloadRef.current) return;
      await loadApprovalQueue(approvalPayloadRef.current, page);
      return;
    }

    await onQueryPageChange?.(page);
  };

  const handleRefer = async () => {
    if (isViewOnly || actionBusy) return;
    if (!reviewMainCategory || !reviewCategory) {
      alert('Must Select DATA');
      return;
    }
    if (!reviewQuery || !reviewSolution) {
      alert('Query OR Solution must Filled !');
      return;
    }
    const complaintId = Number(selectedQid);
    if (!Number.isFinite(complaintId)) {
      alert('Invalid Query ID.');
      return;
    }

    const payload = {
      ComplaintID: complaintId,
      ModifiedBy: currentUserId,
      JobStatus: 'Refer',
      CategoryName: reviewMainCategory,
      SubCategoryName: reviewCategory,
      ResolutionProvided: detail?.resolutionProvided || '',
      Query: reviewQuery,
      Solution: reviewSolution,
      CategoryID: detail?.categoryId || 0,
      SubCategoryID: detail?.subCategoryId || 0,
      RoutedToDepartment: routedToDepartmentName,
      computernameDetails,
      Shortquery: isShortQuery ? '1' : '0'
    };

    try {
      setActionBusy(true);
      const res = await fetch(`${API_BASE}/complaints/refer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }
      if (!res.ok) {
        alert(result?.message || `Refer failed: ${res.status}`);
        return;
      }
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              jobStatus: 'Refer',
              modifiedBy: String(currentUserId || ''),
              modifiedOn: new Date().toISOString(),
              routedToDepartment: routedToDepartmentName || prev.routedToDepartment,
            }
          : prev
      );
      await refreshApprovalQueue();
      alert(result?.message || 'Query Updated Successfuly');
    } catch (error) {
      alert(`Refer error: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  };

  const handleCloseDecision = async () => {
    if (isViewOnly || actionBusy || closeLabel !== 'Close') return;
    if (!reviewMainCategory || !reviewCategory) {
      alert('Must Select DATA');
      return;
    }
    if (!reviewQuery || !reviewSolution) {
      alert('Query and Solution must filled !');
      return;
    }

    const complaintId = Number(selectedQid);
    if (!Number.isFinite(complaintId)) {
      alert('Invalid Query ID.');
      return;
    }

    if (!['completed', 'complete', 'advised', 'corrected'].includes(selectedStatusNormalized)) {
      alert('Unknown Status.');
      return;
    }

    if (!currentUserId) {
      alert('UserID missing. Please re-login.');
      return;
    }

    const payload = {
      ComplaintID: complaintId,
      ModifiedBy: currentUserId,
      ClosedBy: currentUserId,
      JobStatus: 'Closed',
      CategoryName: reviewMainCategory,
      SubCategoryName: reviewCategory,
      ChildCatName: reviewSubCategory || '',
      AdditionalInfo5: reviewSubCategory || '',
      childcat: reviewSubCategory || '',
      ResolutionProvided: detail?.resolutionProvided || '',
      Query: reviewQuery,
      Solution: reviewSolution,
      CategoryID: detail?.categoryId || 0,
      SubCategoryID: detail?.subCategoryId || 0,
      RoutedToDepartment: routedToDepartmentName,
      computernameDetails:
        computernameDetails ||
        (typeof window !== 'undefined' ? window.location.hostname : ''),
      Shortquery: isShortQuery ? '1' : '0',
    };

    try {
      setActionBusy(true);
      const res = await fetch(`${API_BASE}/complaints/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }
      if (!res.ok) {
        alert(result?.message || `Close failed: ${res.status}`);
        return;
      }

      setDetail((prev) =>
        prev
          ? {
              ...prev,
              jobStatus: 'Closed',
              closedBy: String(currentUserId),
              closedOn: new Date().toISOString(),
              routedToDepartment: routedToDepartmentName || prev.routedToDepartment,
            }
          : prev
      );
      await refreshApprovalQueue();
      alert(result?.message || 'Query Updated Successfuly');
    } catch (error) {
      alert(`Close error: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  };

  const handleApproveDecision = async () => {
    if (isViewOnly || actionBusy) return;
    if (!reviewMainCategory || !reviewCategory) {
      alert('Must Select DATA');
      return;
    }
    if (!reviewQuery || !reviewSolution) {
      alert('Query and Solution must filled !');
      return;
    }

    const complaintId = Number(selectedQid);
    if (!Number.isFinite(complaintId)) {
      alert('Invalid Query ID.');
      return;
    }

    if (!['completed', 'complete', 'advised', 'corrected'].includes(selectedStatusNormalized)) {
      alert('Unknown Status.');
      return;
    }

    if (!currentUserId) {
      alert('UserID missing. Please re-login.');
      return;
    }

    const payload = {
      ComplaintID: complaintId,
      ModifiedBy: currentUserId,
      JobStatus: 'Approved',
      CategoryName: reviewMainCategory,
      SubCategoryName: reviewCategory,
      ChildCatName: reviewSubCategory || '',
      AdditionalInfo5: reviewSubCategory || '',
      childcat: reviewSubCategory || '',
      ResolutionProvided: detail?.resolutionProvided || '',
      Query: reviewQuery,
      Solution: reviewSolution,
      CategoryID: detail?.categoryId || 0,
      SubCategoryID: detail?.subCategoryId || 0,
      RoutedToDepartment: routedToDepartmentName,
      computernameDetails:
        computernameDetails ||
        (typeof window !== 'undefined' ? window.location.hostname : ''),
      Shortquery: isShortQuery ? '1' : '0',
    };

    try {
      setActionBusy(true);
      const res = await fetch(`${API_BASE}/complaints/approved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let result = null;
      try { result = text ? JSON.parse(text) : null; } catch { result = { message: text }; }
      if (!res.ok) {
        alert(result?.message || `Approve failed: ${res.status}`);
        return;
      }

      setDetail((prev) =>
        prev
          ? {
              ...prev,
              jobStatus: 'Approved',
              modifiedBy: String(currentUserId),
              modifiedOn: new Date().toISOString(),
              routedToDepartment: routedToDepartmentName || prev.routedToDepartment,
            }
          : prev
      );
      await refreshApprovalQueue();
      alert(result?.message || 'Query Updated Successfuly');
    } catch (error) {
      alert(`Approve error: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  };

  if (!isActive) return null;

  return (
    <section className="module-panel active" id="module-approval">
      <div className="approval-layout">
        <section className="admin-stage">
          <div className="toolbar panel reveal is-visible">
            <div className="toolbar-group">
              <label>
                <span>Date From</span>
                <input type="date" name="dateFrom" value={approvalFilters.dateFrom} onChange={handleApprovalFilterChange} />
              </label>
              <label>
                <span>Date To</span>
                <input type="date" name="dateTo" value={approvalFilters.dateTo} onChange={handleApprovalFilterChange} />
              </label>
              <label>
                <span>Status</span>
                <select name="status" value={approvalFilters.status} onChange={handleApprovalFilterChange}>
                  {APPROVAL_FILTER_STATUSES.map((status) => (
                    <option key={status} value={status === '--Select--' ? '' : status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="toolbar-actions">
              <button className="btn btn-soft" type="button" onClick={handleApprovalFilterReset} disabled={approvalLoading}>Reset</button>
              <button className="btn btn-primary" type="button" onClick={handleApprovalFilterApply} disabled={approvalLoading}>
                {approvalLoading ? 'Applying...' : 'Apply Filter'}
              </button>
            </div>
          </div>
          {approvalErr && <div className="query-history-empty">{approvalErr}</div>}

          <div className="approval-content">
            <section className="panel queue-panel reveal is-visible">
              <div className="panel-head">
                <h3>Lawyer Queries Waiting for Approval</h3>
                <span className="status-chip warning">{queuePendingCount} Pending</span>
              </div>
              <div className="queue-filter-row">
                <label>
                  <span>Lawyer Selection</span>
                  <select value={selectedLawyer} onChange={(e) => setSelectedLawyer(e.target.value)}>
                    <option value="all">All Lawyers</option>
                    {lawyerOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={`queue-list ${queueRows.length ? 'queue-list-fill' : ''}`}>
                {queueRows.map((row) => {
                  const key = getRowKey?.(row) || row.queryId || row.QueryID || '';
                  const statusClass = statusToClass?.(row.status || row.Status);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`queue-item ${key === selectedApprovalId ? 'active' : ''}`}
                      onClick={() => onSelectApproval?.(key)}
                    >
                      <div className="queue-line">
                        <div className="queue-title">{row.queryId || row.QueryID || key}</div>
                        <span className={`queue-status ${statusClass}`}>{row.status || row.Status || 'Pending'}</span>
                      </div>
                      <div className="queue-meta">{row.contactPerson || row.ContactPerson || ''}</div>
                      <div className="queue-meta">{row.contactNumber || row.ContactNumber || ''}</div>
                    </button>
                  );
                })}
                {!queueRows.length && approvalLoading && <div className="queue-empty">Loading approvals...</div>}
                {!queueRows.length && !approvalLoading && <div className="queue-empty">No approvals loaded.</div>}
              </div>
              <div className="results-pager">
                <button
                  className="btn btn-soft"
                  type="button"
                  onClick={() => handleQueuePageChange(queueCurrentPage - 1)}
                  disabled={queueCurrentPage <= 1 || queuePageLoading}
                >&lt;&lt;</button>
                <span style={{ alignSelf: 'center', fontSize: '0.85rem', padding: '0 8px' }}>
                  {queueCurrentPage} / {queueTotalPages}
                </span>
                <button
                  className="btn btn-soft"
                  type="button"
                  onClick={() => handleQueuePageChange(queueCurrentPage + 1)}
                  disabled={queueCurrentPage >= queueTotalPages || queuePageLoading}
                >&gt;&gt;</button>
              </div>
              <div className="queue-footer">
                {!isViewOnly && (
                  <button className="btn btn-soft" type="button" id="closeGiBtn" onClick={onCloseGI}>
                    Close GI
                  </button>
                )}
                <span className="queue-gi-count">GIs: {queueTotalCount}</span>
              </div>
            </section>

            <section className="panel review-panel reveal is-visible">
              <div className="panel-head">
                <h3>Chief Legal Advisor Review</h3>
                <span className={`status-chip ${selectedStatusClass || ''}`}>{selectedStatus}</span>
              </div>

              <div className="review-screen-tabs">
                <button
                  className={`review-screen-tab ${activeScreen === 'advisor' ? 'active' : ''}`}
                  type="button"
                  onClick={() => onReviewScreenChange?.('advisor')}
                >
                  Advisor Decision
                </button>
                <button
                  className={`review-screen-tab ${activeScreen === 'caller' ? 'active' : ''}`}
                  type="button"
                  onClick={() => onReviewScreenChange?.('caller')}
                >
                  Callers Information
                </button>
                <button
                  className={`review-screen-tab ${activeScreen === 'history' ? 'active' : ''}`}
                  type="button"
                  onClick={() => onReviewScreenChange?.('history')}
                >
                  Query History
                </button>
              </div>

              <div className={`review-screen-pane ${activeScreen === 'advisor' ? 'active' : ''}`}>
                <div className="review-grid">
                  <label>
                    <span>Query ID</span>
                    <input type="text" readOnly value={selectedQid} />
                  </label>
                  <label>
                    <span>Submitted By (Lawyer)</span>
                    <input type="text" readOnly value={reviewSubmittedBy} />
                  </label>
                  <label>
                    <span>Main Category</span>
                    <input type="text" readOnly value={reviewMainCategory} />
                  </label>
                  <label>
                    <span>Priority</span>
                    <input type="text" readOnly value={reviewPriority} />
                  </label>
                  <label>
                    <span>Submitted On</span>
                    <input type="text" readOnly value={reviewSubmittedOn} />
                  </label>
                  <label>
                    <span>Caller</span>
                    <input type="text" readOnly value={reviewCaller} />
                  </label>
                </div>
                {detailLoading && <div className="query-history-empty">Loading detail…</div>}
                {detailErr && <div className="query-history-empty">{detailErr}</div>}

                <div className="text-areas">
                  <label>
                    <span>Query</span>
                    <textarea rows="4" readOnly value={reviewQuery}></textarea>
                  </label>
                  <label>
                    <span>Lawyer Provided Solution</span>
                    <textarea rows="4" readOnly value={reviewSolution}></textarea>
                  </label>
                </div>

                <section className="advisor-extra-fields">
                  <div className="advisor-extra-left">
                    <label>
                      <span>Main Category</span>
                      <input type="text" readOnly value={reviewMainCategory} />
                    </label>
                    <label>
                      <span>Category</span>
                      <input type="text" readOnly value={reviewCategory} />
                    </label>
                    <label>
                      <span>Sub Category</span>
                      <input type="text" readOnly value={reviewSubCategory} />
                    </label>
                    <label>
                      <span>Main Tags</span>
                      <input type="text" readOnly value={detail?.mainTags || ''} />
                    </label>
                    <label>
                      <span>MFL Tags</span>
                      <input type="text" readOnly value={detail?.tagMfl || ''} />
                    </label>
                    <label>
                      <span>WRLMP Tags</span>
                      <input type="text" readOnly value={detail?.tagWrlmp || ''} />
                    </label>
                  </div>

                  <div className="advisor-extra-footer">
                    <button className="btn btn-soft" type="button">View Notes</button>
                    <div className="advisor-tag-strip">
                      <span className="tag-strip-label">Tags Selected</span>
                      <div className="advisor-tag-checks">
                        {displayTags.map((tag) => {
                          const checked = hasTagData ? tagSet.has(normalizeTag(tag)) : false;
                          return (
                          <label className="tag-chip" key={tag}>
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                            />
                            <span>{tag}</span>
                          </label>
                          );
                        })}
                      </div>
                    </div>
                    <label className="advisor-dept-select">
                      <span>Sindh Gov Department</span>
                      <select
                        value={departmentSelection || '--Select--'}
                        onChange={(e) => setDepartmentSelection(e.target.value)}
                        disabled={isViewOnly}
                      >
                        {departmentOptions.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </section>

                {/* <label className="remarks-box">
                  <span>Chief Advisor Remarks</span>
                  <textarea rows="3" placeholder="Add decision remarks for audit trail"></textarea>
                </label> */}

                <div className="decision-bar">
                  {!isViewOnly && !isAdvisedStatus && showApprovedButton && (
                    <button className="btn btn-success" type="button" onClick={handleApproveDecision} disabled={actionBusy}>
                      {actionBusy ? 'Approving...' : 'Approved'}
                    </button>
                  )}
                  {!isViewOnly && showRefer && (
                    <button className="btn btn-success" type="button" onClick={handleRefer} disabled={actionBusy}>Reffer</button>
                  )}
                  {!isViewOnly && showClose && (
                    <button
                      className="btn btn-success"
                      type="button"
                      onClick={closeLabel === 'Approved' ? handleApproveDecision : handleCloseDecision}
                      disabled={actionBusy}
                    >
                      {actionBusy && closeLabel === 'Close'
                        ? 'Closing...'
                        : actionBusy && closeLabel === 'Approved'
                        ? 'Approving...'
                        : closeLabel}
                    </button>
                  )}
                  {!isViewOnly && !isAdvisedStatus && showRejectButton && (
                    <button className="btn btn-danger" type="button">Reject</button>
                  )}
                  <span className="decision-note">Decision not submitted yet.</span>
                </div>

                <div className="advisor-extra-grid advisor-audit-grid">
                  <label>
                    <span>Launched By</span>
                    <input type="text" readOnly value={reviewSubmittedBy} />
                  </label>
                  <label>
                    <span>Accepted By</span>
                    <input type="text" readOnly value={detail?.routedBy || selectedApproval?.routedBy || selectedApproval?.RoutedBy || ''} />
                  </label>
                  <label>
                    <span>Launched On</span>
                    <input type="text" readOnly value={reviewSubmittedOn} />
                  </label>
                  <label>
                    <span>Accepted On</span>
                    <input type="text" readOnly value={detail?.routedOn || selectedApproval?.routedOn || selectedApproval?.RoutedOn || ''} />
                  </label>
                </div>
              </div>

              <div className={`review-screen-pane ${activeScreen === 'caller' ? 'active' : ''}`}>
                <CallerInformationTab row={selectedApproval} />
              </div>

              <div className={`review-screen-pane ${activeScreen === 'history' ? 'active' : ''}`}>
                <div className="query-history-wrap">
                  <table className="query-history-table">
                    <thead>
                      <tr>
                        <th>QueryID</th>
                        <th>QueryStatus</th>
                        <th>CreatedOn</th>
                        <th>CreatedBy</th>
                        <th>ModifiedOn</th>
                        <th>ModifiedBy</th>
                        <th>ClosedOn</th>
                        <th>ClosedBy</th>
                        <th>ShortQuery</th>
                        <th>Query</th>
                        <th>Solution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyLoading && (
                        <tr>
                          <td colSpan={11} className="query-history-empty">Loading history…</td>
                        </tr>
                      )}
                      {!historyLoading && historyErr && (
                        <tr>
                          <td colSpan={11} className="query-history-empty">{historyErr}</td>
                        </tr>
                      )}
                      {!historyLoading && !historyErr && historyRows.map((row, idx) => (
                        <tr key={`${row.queryId || 'row'}-${idx}`}>
                          <td>{row.queryId}</td>
                          <td>{row.status}</td>
                          <td>{row.createdOn}</td>
                          <td>{row.createdBy}</td>
                          <td>{row.modifiedOn}</td>
                          <td>{row.modifiedBy}</td>
                          <td>{row.closedOn || '-'}</td>
                          <td>{row.closedBy || '-'}</td>
                          <td className="cell-query">{row.shortQuery}</td>
                          <td className="cell-query">{row.query}</td>
                          <td className="cell-solution">{row.solution}</td>
                        </tr>
                      ))}
                      {!historyLoading && !historyErr && !historyRows.length && (
                        <tr>
                          <td colSpan={11} className="query-history-empty">No history loaded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>
    </section>
  );
};

export default BackofficeApprovalPanel;
